import type { RichText } from '@diana/contracts';

import { CompactRichTextField } from './CompactRichTextField.js';

type RichTextFieldProps = {
  label: string;
  value: RichText | undefined;
  optional?: boolean;
  variant?: 'compact' | 'extended';
  onChange(value: RichText | undefined): void;
};

export function RichTextField({
  label,
  value,
  optional,
  variant = 'compact',
  onChange,
}: RichTextFieldProps) {
  return (
    <CompactRichTextField
      label={label}
      value={value ?? plainToRichText('')}
      variant={variant}
      onChange={(next) => onChange(optional && richTextIsEmpty(next) ? undefined : next)}
    />
  );
}

export function richTextToPlain(value: RichText | undefined) {
  if (!value) return '';
  return value.children
    .map((node) => {
      if (node.type === 'bulletList') {
        return node.items
          .map((item) => `• ${item.map((child) => ('text' in child ? child.text : '')).join('')}`)
          .join('\n');
      }
      return node.children.map((child) => ('text' in child ? child.text : '')).join('');
    })
    .join('\n\n');
}

export function plainToRichText(value: string): RichText {
  const paragraphs = value.split(/\n{2,}/).filter((part) => part.length > 0);
  return {
    type: 'document',
    children: (paragraphs.length > 0 ? paragraphs : ['']).map((text) => ({
      type: 'paragraph',
      children: [{ type: 'text', text }],
    })),
  };
}

function richTextIsEmpty(value: RichText) {
  return value.children.every((node) =>
    node.type === 'bulletList'
      ? node.items.every((item) =>
          item.every((child) =>
            child.type === 'link'
              ? child.children.every((text) => text.text.trim().length === 0)
              : child.text.trim().length === 0,
          ),
        )
      : node.children.every((child) =>
          child.type === 'link'
            ? child.children.every((text) => text.text.trim().length === 0)
            : child.text.trim().length === 0,
        ),
  );
}
